import { SectionHandler } from '../entity.handler';
import { AssetUpdateRequest, Contact } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { TextField, ComboField } from '../utils';
import * as uuid from 'uuid';

export interface ContactsSectionValues {
    contacts: Contact[];
}

export interface ContactEntity {
    id: string;
    contacts?: Contact[];
}

export class ContactsSectionHandler implements SectionHandler<ContactEntity, ContactsSectionValues> {

    type = 'contacts';
    icon = 'address-card';
    bulk: true;

    title = this.translate('TITLE'); 
    description = this.translate(`DESC_${this.entityType.toUpperCase()}`);

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public entityType: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.CONTACTS.${key}`, values);
    }

    applyUpdate(entity: ContactEntity, payload: any): Promise<any> {
        switch (this.entityType) {
            case 'asset': return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
            case 'assetgroup': return this.admin.applyUpdate(api => api.entities.updateAssetGroup(entity.id, payload));
            case 'assetcategory': return this.admin.applyUpdate(api => api.entities.updateAssetCategory(entity.id, payload));
            case 'costcentre': return this.admin.applyUpdate(api => api.entities.updateCostCentre(entity.id, payload));
            default: return Promise.reject(new Error(`Cannot update contacts for entity type "${this.entityType}"`));
        }
    }

    mapEntityToEditor(entity: ContactEntity): Promise<ContactsSectionValues> {
        return Promise.resolve({ contacts: entity.contacts });
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: ContactsSectionValues): Promise<AssetUpdateRequest> {
        entity.contacts = values.contacts;
        return Promise.resolve(entity);
    }

    getContactTypeLookupValues(): { key: string, value: string }[] {
        return ['owner', 'emergency', 'administrative', 'technical', 'operational', 'management'].map(x => {
            return {
                key: x,
                value: this.admin.i18n.instant(`SHARED.CONTACTS.TYPES.${x.toUpperCase()}`), 
            };
        });
    }

    showContactModal(values: Contact[], item: Contact): Promise<Contact> {
        return this.admin.showFormModal<Contact>({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                    ComboField('type', this.translate('FIELDS.TYPE'), true, this.getContactTypeLookupValues()),
                    TextField('emailAddress', this.translate('FIELDS.EMAIL'), false, 1, 255),
                    TextField('phoneMobile', this.translate('FIELDS.MOBILE'), false, 1, 255),
                    TextField('phoneOffice', this.translate('FIELDS.PHONE'), false, 1, 255),
                    TextField('notes', this.translate('FIELDS.NOTES'), false, 1, 1024),
                ],
            }],
        }, item || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            type: 'owner',
        }
        ).then(contact => {
            if (contact) {
                const idx = values.findIndex(x => x.id === contact.id);
                if (idx === -1) {
                    values.push(contact);
                } else {
                    values[idx] = contact;
                }
            }
            return contact;
        });


    }

}
